import {
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  SetFilterValuesFuncParams,
  ColumnState,
} from 'ag-grid-community';
import { map } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { GraphicListColumn } from '../models/ag-grid-list-column.model';
import { GridConfig } from '../models/grid-config.model';
import { AgGridUtil } from './ag-grid-util';
import { CONSTANTS_VALUE } from '../../config/constants.module';
import { AgGridListViewService } from '../services/ag-grid-list-view.service';

export class AgGridDataSource implements IServerSideDatasource {
  private readonly columns: GraphicListColumn[];
  private firstLoad = true;

  constructor(
    private readonly gridConfig: GridConfig,
    private readonly tabId: string,
    private agGridlistViewService: AgGridListViewService,
    public selection: number[]
  ) {
    this.columns = gridConfig.columns;
  }

  /**
   * @function getRows
   * @param params
   * @see https://www.ag-grid.com/javascript-grid-server-side-model-datasource/
   * @description AgGrid Interface method. Called on every changes of the Grid.
   */
  public getRows(params: IServerSideGetRowsParams): void {
    const agGridRequest: IServerSideGetRowsRequest = params.request;
    if (
      this.firstLoad &&
      this.gridConfig.filterModel &&
      !AgGridUtil.isFilterModelInSync(params.api, this.gridConfig.filterModel)
    ) {
      // console.log('ignore request, waiting for filters to be in sync');
      params.fail();
    } else {
      this.firstLoad = false;

      forkJoin([
        this.agGridlistViewService.loadFeaturesServerSide(agGridRequest, this.tabId),
        this.agGridlistViewService.getAgGridFeaturesCount(agGridRequest, this.tabId),
      ])
        .pipe(
          map(result => {
            const rawFeatures = result[0] || [];
            const rowData = rawFeatures.map(x => this.transformToRowData(x, this.columns));
            return { resultsForPage: rowData, rowCount: result[1] };
          })
        )
        .subscribe(
          result => {
            params?.success({ rowData: result.resultsForPage, rowCount: result.rowCount });
            this.setGridSelection(result.resultsForPage, this.selection, params.api);
            this.gridConfig?.onGetRowDataFinished();
          },
          error => {
            console.log(error);
            params.fail();
          }
        );
    }
  }

  public getRowIndex(objectId, columnState: ColumnState[], totalRows: number): Observable<number> {
    return this.agGridlistViewService.loadAllFeatureIds(columnState, this.tabId, totalRows).pipe(
      map(featureIds => {
        return featureIds.indexOf(parseInt(objectId));
      })
    );
  }

  /**
   * @function loadDomainValues
   * @param params
   * @description load domain values by given task
   */
  public loadDomainValues(params: SetFilterValuesFuncParams): Observable<any> {
    return this.getDomainForSetFilter(params).pipe(
      map((featureSet: __esri.FeatureSet) => {
        return this.parseDomainValuesToArray(params, featureSet);
      })
    );
  }

  public destroy?(): void {}

  /**
   * @function transformToRowData
   * @param feature
   * @param columns
   * @description tranform feature Type into rowData for AgGrid to
   * display
   */
  private transformToRowData(feature: any, columns: GraphicListColumn[]): {} {
    const rowDataItem = {};
    columns.forEach(column => {
      const fieldName = this.friendlyFieldPathName(column.field);
      rowDataItem[fieldName] = this.getFieldValue(feature, fieldName);
    });
    return rowDataItem;
  }

  private getFieldValue(feature: any, fieldName: string): any {
    if (feature.attributes && feature.attributes[fieldName] !== null) {
      return feature.attributes[fieldName];
    }
    return null;
  }

  private friendlyFieldPathName(field) {
    return field.replace('feature.attributes.', '');
  }

  /**
   * @function getDomainForSetFilter
   * @param params
   */
  private getDomainForSetFilter(params: SetFilterValuesFuncParams): Observable<__esri.FeatureSet> {
    return this.agGridlistViewService.getDomainForSetFilter([params.colDef.field], this.tabId);
  }

  /**
   * @function parseDomainValuesToArray
   * @param params
   * @param featureSet
   * @description parse through given featureSet and get all domain values
   * matching given field in params.colDef
   */
  private parseDomainValuesToArray(params: SetFilterValuesFuncParams, featureSet: __esri.FeatureSet): any[] {
    const setFilterValues = new Set();
    featureSet.features.forEach(f => {
      const value =
        f.attributes[params.colDef.field] === null
          ? CONSTANTS_VALUE.agGrid.emptyFilterValue
          : f.attributes[params.colDef.field];
      setFilterValues.add(value);
    });
    return Array.from(setFilterValues);
  }

  private setGridSelection(rows: any[], selection: number[], api: GridApi) {
    rows.forEach(row => {
      if (row.OBJECTID && selection.includes(row.OBJECTID)) {
        const rowNode = api.getRowNode(row.OBJECTID.toString());
        if (rowNode) {
          rowNode.setSelected(true);
        }
      }
    });
  }
}
